<template>
  <div class="view-container">
    <div class="main-layout">
      <!-- 左侧面板：公告列表 -->
      <div class="left-panel">
        <div class="panel-header"><h4>公告列表</h4></div>
        <div class="panel-body">
          <table class="tree-table" v-if="paginatedAnnouncements.length > 0">
            <thead><tr><th>公告名称</th></tr></thead>
            <tbody>
              <tr
                v-for="a in paginatedAnnouncements"
                :key="a.guid"
                class="tree-row"
                :class="{ selected: selectedAnnouncementGuid === a.guid }"
                @click="selectAnnouncement(a)"
              >
                <td>
                  <span class="tree-name-cell">
                    <i class="fas fa-bullhorn"></i>
                    <span class="tree-name">{{ a.titles }}</span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-left">暂无公告</div>

          <!-- 左侧分页 -->
          <div class="left-pagination" v-if="announcementTotalPages > 1">
            <button @click="announcementPrevPage" :disabled="announcementCurrentPage === 1" class="btn btn-default btn-sm pagination-btn">
              <i class="fas fa-chevron-left"></i>
            </button>
            <div class="page-numbers">
              <button
                v-for="page in announcementVisiblePages"
                :key="page"
                @click="announcementGoToPage(page)"
                :class="['page-btn', { active: announcementCurrentPage === page, disabled: page === '...' }]"
                :disabled="page === '...'"
              >{{ page }}</button>
            </div>
            <button @click="announcementNextPage" :disabled="announcementCurrentPage >= announcementTotalPages" class="btn btn-default btn-sm pagination-btn">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- 右侧面板：权限列表 -->
      <div class="right-panel">
        <div class="panel-body">
          <div v-if="selectedAnnouncementGuid">
            <!-- 搜索栏 -->
            <div class="search-panel">
              <div class="search-row">
                <div class="search-item">
                  <label><i class="fas fa-bullhorn"></i> 公告名称</label>
                  <input v-model="searchForm.announcementName" type="text" placeholder="搜索公告" class="search-input">
                </div>
                <div class="search-item">
                  <label><i class="fas fa-tag"></i> 授权类型</label>
                  <select v-model="searchForm.targetType" class="search-select">
                    <option value="">全部</option>
                    <option value="User">用户</option>
                    <option value="Org">组织</option>
                    <option value="Role">角色</option>
                  </select>
                </div>
                <div class="search-actions">
                  <button @click="handleSearch" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
                  <button @click="resetSearch" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
                  <button @click="openAddDialog" class="btn btn-success search-btn"><i class="fas fa-plus"></i> 新增权限</button>
                </div>
              </div>
            </div>

            <!-- 权限表格 -->
            <div class="table-container-inner">
              <table class="data-table">
                <thead><tr>
                  <th>公告名称</th><th width="80">授权类型</th><th>授权对象</th><th width="80">权限范围</th><th width="70">状态</th><th width="120">操作</th>
                </tr></thead>
                <tbody>
                  <tr v-for="p in paginatedData" :key="p.id">
                    <td>{{ p.announcementName }}</td>
                    <td><span class="type-badge">{{ typeLabels[p.operatorType] || p.operatorType }}</span></td>
                    <td>{{ p.targetName }}</td>
                    <td><span>{{ p.scope === '1' ? '可查看' : '拒绝' }}</span></td>
                    <td><span class="status-badge" :class="p.status==='启用'?'enabled':'disabled'">{{ p.status }}</span></td>
                    <td class="action-cell">
                      <button @click="editPermission(p)" class="btn btn-primary btn-xs" title="编辑"><i class="fas fa-edit"></i></button>
                      <button @click="toggleStatus(p)" class="btn btn-xs" :class="p.status==='启用'?'btn-warning':'btn-success'" :title="p.status==='启用'?'禁用':'启用'"><i class="fas" :class="p.status==='启用'?'fa-ban':'fa-check'"></i></button>
                      <button @click="deletePermission(p)" class="btn btn-danger btn-xs" title="删除"><i class="fas fa-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="paginatedData.length === 0"><td colspan="6" class="empty-cell">暂无权限数据</td></tr>
                </tbody>
              </table>
            </div>

            <!-- 分页 -->
            <div class="pagination-row">
              <div class="pagination-info">共 {{ totalCount }} 条记录</div>
              <div class="pagination-controls">
                <div class="page-size-selector"><span>每页</span><select v-model="pageSize" class="page-size-input form-control"><option :value="20">20</option><option :value="50">50</option><option :value="75">75</option><option :value="100">100</option></select><span>条</span></div>
                <button @click="prevPage" :disabled="currentPage===1" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-left"></i></button>
                <div class="page-numbers"><button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="['page-btn',{active:currentPage===page,disabled:page==='...'}]" :disabled="page==='...'">{{ page }}</button></div>
                <button @click="nextPage" :disabled="currentPage>=totalPages" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-right"></i></button>
                <div class="page-jump"><span>跳转到</span><input type="number" v-model="jumpPage" min="1" :max="totalPages" class="jump-input" @keyup.enter="handleJump"><span>页</span><button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button></div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state"><i class="fas fa-bullhorn"></i><p>请选择一个公告</p></div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-container">
        <div class="modal-header"><h3>{{ editingId ? '编辑权限' : '新增权限' }}</h3><button @click="closeFormModal" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-row"><div class="form-group"><label>公告 *</label><input v-model="form.announcementName" class="form-control" readonly></div></div>
          <div class="form-row">
            <div class="form-group"><label>授权类型 *</label><select v-model="form.operatorType" class="form-control"><option value="User">用户</option><option value="Org">组织</option><option value="Role">角色</option></select></div>
            <div class="form-group"><label>授权对象 *</label><div class="search-select"><input v-model="form.targetName" class="form-control" readonly @click="openTargetPicker"><button @click="openTargetPicker" class="search-btn"><i class="fas fa-search"></i></button></div></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>权限范围</label><select v-model="form.scope" class="form-control"><option value="1">可查看</option><option value="0">拒绝</option></select></div>
            <div class="form-group"><label>状态</label><select v-model="form.status" class="form-control"><option value="启用">启用</option><option value="禁用">禁用</option></select></div>
          </div>
          <div class="form-group"><label>备注</label><textarea v-model="form.remark" class="form-control" rows="2" placeholder="备注信息"></textarea></div>
        </div>
        <div class="modal-footer"><button @click="closeFormModal" class="btn btn-default">取消</button><button @click="saveForm" class="btn btn-primary">保存</button></div>
      </div>
    </div>

    <!-- 选择授权对象弹窗 -->
    <div v-if="showTargetPicker" class="modal-overlay" @click.self="showTargetPicker=false">
      <div class="modal-container picker-modal">
        <div class="modal-header"><h3>选择{{ targetTypeLabel }}</h3><button @click="showTargetPicker=false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="search-box">
            <div class="search-input-wrap">
              <i class="fas fa-search search-icon"></i>
              <input v-model="targetSearch" class="search-box-input" :placeholder="'搜索' + targetTypeLabel" @input="onTargetSearch">
            </div>
          </div>
          <div class="picker-list">
            <template v-if="form.operatorType === 'User'">
              <div v-for="item in filteredTargets" :key="item.id" class="picker-item" @click="selectTarget(item)" :class="{ selected: form.operatorID === item.id }">
                <i class="fas fa-user"></i>
                <span>{{ item.name }}</span>
              </div>
            </template>
            <template v-else>
              <div v-for="item in filteredTargets" :key="item.id" class="picker-item tree-item" :class="['tree-level-' + (item.level || 0), { selected: form.operatorID === item.id }]" @click="selectTarget(item)">
                <i class="fas" :class="form.operatorType==='Org'?'fa-building':'fa-user-tag'"></i>
                <span>{{ item.name }}</span>
                <span v-if="item.hasChildren" class="tree-hint">···</span>
              </div>
            </template>
            <div v-if="filteredTargets.length === 0" class="picker-empty">无匹配结果</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getAnnouncementList } from '../api/announcement'
import { getAnnouncementPermissions, saveAnnouncementPermission, deleteAnnouncementPermission } from '../api/permission'
import { getUserList } from '../api/user'
import { getOrgTree } from '../api/org'
import { getRoleTree } from '../api/role'

const typeLabels = { User: '用户', Org: '组织', Role: '角色' }

// 右侧权限表格分页
const currentPage = ref(1)
const pageSize = ref(20)
const jumpPage = ref(1)

// 左侧公告列表面板分页
const announcementCurrentPage = ref(1)
const announcementPageSize = ref(15)

const selectedAnnouncementGuid = ref(null)
const showFormModal = ref(false)
const showTargetPicker = ref(false)
const editingId = ref(null)
const targetSearch = ref('')

const searchForm = reactive({ announcementName: '', targetType: '' })
const form = reactive({ id: null, guid: null, announcementId: null, announcementName: '', operatorType: 'User', operatorID: null, targetName: '', scope: '1', status: '启用', remark: '' })

const announcements = ref([])
const permissions = ref([])
const users = ref([])
const orgs = ref([])       // 扁平列表（权限行名称对照用）
const rls = ref([])        // 扁平列表（权限行名称对照用）
const orgTree = ref([])    // 树形结构（选择弹窗用）
const roleTree = ref([])   // 树形结构（选择弹窗用）

/* ========== 权限数据加载 ========== */
async function loadPermissionsForAnnouncement(announcementGuid) {
  try {
    const res = await getAnnouncementPermissions(announcementGuid)
    const permList = Array.isArray(res) ? res : (res?.data || [])
    const announcement = announcements.value.find(a => a.guid === announcementGuid)
    const announcementName = announcement ? announcement.titles : ''
    let idSeq = 1
    permissions.value = permList.map(p => {
      const opType = p.operatorType || ''
      const opID = p.operatorID || ''
      let targetName = p.operatorPara1 || opID || ''
      if (opType === 'User') {
        const u = users.value.find(u => u.id === opID)
        if (u) targetName = u.name
      } else if (opType === 'Org') {
        const o = orgs.value.find(o => o.id === opID)
        if (o) targetName = o.name
      } else if (opType === 'Role') {
        const r = rls.value.find(r => r.id === opID)
        if (r) targetName = r.name
      }
      const alloy = p.alloyPermission || ''
      return {
        id: idSeq++,
        guid: p.guid,
        announcementGuid: p.resourceID || announcementGuid,
        announcementName: announcementName,
        operatorType: opType,
        operatorID: opID,
        targetName: targetName,
        scope: alloy.length >= 1 && alloy[0] === '1' ? '1' : '0',
        active: p.active === 1 ? 1 : 0,
        status: p.active === 1 ? '启用' : '禁用',
        remark: p.remark || ''
      }
    })
  } catch (e) {
    console.error('加载公告权限失败', e)
    permissions.value = []
  }
}

/* ========== 基础数据加载 ========== */
async function loadAnnouncements() {
  try {
    const res = await getAnnouncementList()
    announcements.value = (res?.data || [])
  } catch (e) {
    console.error('加载公告列表失败', e)
    announcements.value = []
  }
}

async function loadUsers() {
  try {
    const res = await getUserList()
    const list = Array.isArray(res) ? res : (res?.data || [])
    users.value = list.map(u => ({ id: u.guid, name: u.displayName || u.account || '' }))
  } catch { users.value = [] }
}

async function loadOrgs() {
  try {
    const res = await getOrgTree()
    const list = Array.isArray(res) ? res : (res?.data || [])
    orgTree.value = list
    const flat = (nodes) => {
      let r = []
      for (const n of (nodes || [])) {
        const name = n.ousName || n.oUsName || n.OUsName || ''
        r.push({ id: n.guid, name })
        if (n.children || n.childFolder) r = r.concat(flat(n.children || n.childFolder))
      }
      return r
    }
    orgs.value = flat(list)
  } catch { orgTree.value = []; orgs.value = [] }
}

async function loadRoles() {
  try {
    const res = await getRoleTree()
    const list = Array.isArray(res) ? res : (res?.data || [])
    roleTree.value = list
    const flat = (nodes) => {
      let r = []
      for (const n of (nodes || [])) {
        r.push({ id: n.guid, name: n.roleName || '' })
        if (n.children || n.childFolder) r = r.concat(flat(n.children || n.childFolder))
      }
      return r
    }
    rls.value = flat(list)
  } catch { roleTree.value = []; rls.value = [] }
}

onMounted(() => {
  loadAnnouncements()
  loadUsers()
  loadOrgs()
  loadRoles()
})

/* ========== 左侧公告面板分页 ========== */
const announcementTotalPages = computed(() => Math.ceil(announcements.value.length / announcementPageSize.value) || 1)

const paginatedAnnouncements = computed(() => {
  const s = (announcementCurrentPage.value - 1) * announcementPageSize.value
  return announcements.value.slice(s, s + announcementPageSize.value)
})

const announcementVisiblePages = computed(() => {
  const ps = []
  const t = announcementTotalPages.value
  const c = announcementCurrentPage.value
  if (t <= 7) {
    for (let i = 1; i <= t; i++) ps.push(i)
  } else {
    ps.push(1)
    if (c > 4) ps.push('...')
    for (let i = Math.max(2, c - 2); i <= Math.min(t - 1, c + 2); i++) ps.push(i)
    if (c < t - 3) ps.push('...')
    ps.push(t)
  }
  return ps
})

function announcementGoToPage(p) {
  if (typeof p === 'number' && p >= 1 && p <= announcementTotalPages.value) announcementCurrentPage.value = p
}
function announcementPrevPage() { if (announcementCurrentPage.value > 1) announcementCurrentPage.value-- }
function announcementNextPage() { if (announcementCurrentPage.value < announcementTotalPages.value) announcementCurrentPage.value++ }

/* ========== 选择公告 ========== */
function selectAnnouncement(a) {
  selectedAnnouncementGuid.value = a.guid
  currentPage.value = 1
  loadPermissionsForAnnouncement(a.guid)
}

/* ========== 右侧搜索/分页 ========== */
const filteredData = computed(() => {
  let r = permissions.value
  if (searchForm.announcementName) r = r.filter(p => (p.announcementName || '').includes(searchForm.announcementName))
  if (searchForm.targetType) r = r.filter(p => p.operatorType === searchForm.targetType)
  return r
})
const totalCount = computed(() => filteredData.value.length)
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value) || 1)
const paginatedData = computed(() => {
  const s = (currentPage.value - 1) * pageSize.value
  return filteredData.value.slice(s, s + pageSize.value)
})
const visiblePages = computed(() => {
  const ps = []
  const t = totalPages.value
  const c = currentPage.value
  if (t <= 7) {
    for (let i = 1; i <= t; i++) ps.push(i)
  } else {
    ps.push(1)
    if (c > 4) ps.push('...')
    for (let i = Math.max(2, c - 2); i <= Math.min(t - 1, c + 2); i++) ps.push(i)
    if (c < t - 3) ps.push('...')
    ps.push(t)
  }
  return ps
})

const handleSearch = () => { currentPage.value = 1 }
const resetSearch = () => { Object.keys(searchForm).forEach(k => searchForm[k] = ''); currentPage.value = 1 }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const goToPage = (p) => { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }
const handleJump = () => { const p = parseInt(jumpPage.value); if (p >= 1 && p <= totalPages.value) currentPage.value = p; else jumpPage.value = currentPage.value }

/* ========== 表单操作 ========== */
const openAddDialog = () => {
  editingId.value = null
  const ann = announcements.value.find(a => a.guid === selectedAnnouncementGuid.value)
  Object.assign(form, {
    id: null, guid: null,
    announcementId: selectedAnnouncementGuid.value,
    announcementName: ann ? ann.titles : '',
    operatorType: 'User', operatorID: null, targetName: '',
    scope: '1', status: '启用', remark: ''
  })
  showFormModal.value = true
}

const editPermission = (p) => {
  editingId.value = p.id
  Object.assign(form, {
    id: p.id, guid: p.guid,
    announcementId: p.announcementGuid,
    announcementName: p.announcementName,
    operatorType: p.operatorType,
    operatorID: p.operatorID,
    targetName: p.targetName,
    scope: p.scope,
    status: p.status,
    remark: p.remark || ''
  })
  showFormModal.value = true
}

const closeFormModal = () => { showFormModal.value = false; editingId.value = null }

const saveForm = async () => {
  if (!form.announcementId || !form.operatorID) { $message.warning('请选择授权对象'); return }
  const payload = {
    OperatorType: form.operatorType,
    OperatorID: form.operatorID,
    OperatorPara1: form.targetName,
    ResourceType: 'Announcement',
    ResourceID: form.announcementId,
    AlloyPermission: form.scope === '1' ? '1' : '0',
    Active: form.status === '启用' ? 1 : 0,
    Remark: form.remark || ''
  }
  if (form.guid) payload.Guid = form.guid
  try {
    await saveAnnouncementPermission(payload)
    closeFormModal()
    if (selectedAnnouncementGuid.value) loadPermissionsForAnnouncement(selectedAnnouncementGuid.value)
  } catch (e) { console.error('保存权限失败', e); $message.error('保存失败，请重试') }
}

const toggleStatus = async (p) => {
  const newActive = p.active === 1 ? 0 : 1
  const actionLabel = newActive === 1 ? '启用' : '禁用'
  try {
    await saveAnnouncementPermission({
      Guid: p.guid,
      OperatorType: p.operatorType,
      OperatorID: p.operatorID,
      OperatorPara1: p.targetName,
      ResourceType: 'Announcement',
      ResourceID: p.announcementGuid,
      AlloyPermission: p.scope === '1' ? '1' : '0',
      Active: newActive,
      Remark: p.remark || ''
    })
    p.status = newActive === 1 ? '启用' : '禁用'
    p.active = newActive
    $message.success(`已${actionLabel}`)
  } catch (e) {
    console.error('切换状态失败', e)
    $message.error(`${actionLabel}失败，请重试`)
  }
}

const deletePermission = async (p) => {
  if (!await $confirm('确定删除该权限记录吗？')) return
  try {
    await deleteAnnouncementPermission(p.guid)
    permissions.value = permissions.value.filter(x => x.id !== p.id)
  } catch (e) { console.error('删除权限失败', e); $message.error('删除失败，请重试') }
}

/* ========== 目标选择器 ========== */
const targetTypeLabel = computed(() => ({ User: '用户', Org: '组织', Role: '角色' })[form.operatorType] || '')

/** 将树形数据展平为带层级的列表 */
function flattenForPicker(nodes, level = 0) {
  let result = []
  for (const n of (nodes || [])) {
    const name = form.operatorType === 'Org'
      ? (n.ousName || n.oUsName || n.OUsName || '')
      : (n.roleName || '')
    const children = n.children || n.childFolder || []
    result.push({ id: n.guid, name, level, hasChildren: children.length > 0 })
    if (children.length > 0) {
      result = result.concat(flattenForPicker(children, level + 1))
    }
  }
  return result
}

const filteredTargets = computed(() => {
  if (form.operatorType === 'User') {
    return !targetSearch.value
      ? users.value
      : users.value.filter(i => (i.name || '').includes(targetSearch.value))
  }
  const tree = form.operatorType === 'Org' ? orgTree.value : roleTree.value
  let flat = flattenForPicker(tree)
  if (targetSearch.value) {
    flat = flat.filter(i => (i.name || '').includes(targetSearch.value))
  }
  return flat
})

const onTargetSearch = () => { /* 搜索时自动过滤 */ }

const selectTarget = (item) => { form.operatorID = item.id; form.targetName = item.name; showTargetPicker.value = false }
const openTargetPicker = () => { targetSearch.value = ''; showTargetPicker.value = true }
</script>

<style scoped>
.view-container { padding: 16px; background: #f5f7fa; }
.main-layout { display: flex; gap: 16px; min-height: calc(94vh - 110px); max-height: calc(94vh - 110px); }
.left-panel { flex: 0 0 22%; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; display: flex; flex-direction: column; }
.panel-header { padding: 10px 12px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.panel-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #374151; }
.panel-body { height: 100%; padding: 8px; overflow-y: auto; display: flex; flex-direction: column; }
.right-panel { flex: 1; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; }
.tree-table { width: 100%; border-collapse: collapse; }
.tree-table thead th { padding: 8px 10px; font-size: 11px; font-weight: 600; color: #6b7280; background: #f8fafc; border-bottom: 1px solid #e8ecf1; text-align: left; }
.tree-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; font-size: 12px; color: #374151; }
.tree-row { cursor: pointer; transition: background 0.1s; }
.tree-row:hover { background: #f9fafb; }
.tree-row.selected { background: #ecfdf5; }
.tree-row.selected td { color: #059669; }
.tree-name-cell { display: flex; align-items: center; gap: 4px; }
.tree-name-cell i { color: #10b981; font-size: 11px; }
.tree-name { margin-left: 2px; font-size: 12px; }
.count-badge { margin-left: 4px; background: #ecfdf5; color: #059669; font-size: 10px; padding: 0 5px; border-radius: 8px; min-width: 16px; text-align: center; display: inline-block; line-height: 16px; }

.empty-left { text-align: center; padding: 20px; color: #9ca3af; font-size: 13px; }

/* 左侧分页 */
.left-pagination { display: flex; justify-content: center; align-items: center; gap: 6px; padding: 8px 0; margin-top: auto; border-top: 1px solid #e8ecf1; flex-shrink: 0; }
.left-pagination .pagination-btn { width: 26px; height: 26px; padding: 0; display: flex; align-items: center; justify-content: center; }
.left-pagination .page-btn { width: 26px; height: 26px; font-size: 11px; }
.left-pagination .page-numbers { display: flex; gap: 3px; }

/* 搜索面板 */
.search-panel { padding: 10px 14px; background: #fafbfc; border-bottom: 1px solid #e8ecf1; }
.search-row { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.search-item { display: flex; align-items: center; gap: 6px; }
.search-item label { font-size: 13px; color: #374151; font-weight: 500; white-space: nowrap; }
.search-item label i { color: #10b981; margin-right: 2px; font-size: 12px; }
.search-input, .search-select { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 160px; outline: none; transition: border-color 0.2s; }
.search-input:focus, .search-select:focus { border-color: #10b981; }
.search-actions { display: flex; gap: 6px; margin-left: auto; align-items: center; }
.search-btn { padding: 6px 12px; font-size: 12px; border-radius: 6px; display: flex; align-items: center; gap: 4px; }

.table-container-inner { flex: 1; overflow-y: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 8px 10px; border-bottom: 1px solid #e7eaec; font-size: 12px; color: #676a6c; text-align: left; white-space: nowrap; }
.data-table th { background: #f5f5f5; font-weight: 600; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9fafb; }
.empty-cell { text-align: center; padding: 40px; color: #999; }
.action-cell { white-space: nowrap; text-align: center; }
.action-cell .btn-xs { margin: 0 2px; padding: 4px 8px; font-size: 11px; border-radius: 4px; cursor: pointer; border: 1px solid transparent; }
.btn-primary.btn-xs { background: #3498db; color: #fff; }
.btn-warning.btn-xs { background: #f39c12; color: #fff; }
.btn-danger.btn-xs { background: #e74c3c; color: #fff; }
.btn-success.btn-xs { background: #27ae60; color: #fff; }
.type-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500; background: #f0f4ff; color: #6366f1; }
.status-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500; }
.status-badge.enabled { background: #dcfce7; color: #16a34a; }
.status-badge.disabled { background: #fee2e2; color: #dc2626; }

.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 12px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 8px; }
.page-numbers { display: flex; gap: 4px; }
.page-btn { width: 28px; height: 28px; border: 1px solid #e7eaec; background: #fff; color: #676a6c; border-radius: 3px; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; }
.page-btn:hover:not(.disabled) { border-color: #10b981; color: #10b981; }
.page-btn.active { background: #10b981; border-color: #10b981; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 28px; height: 28px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 4px; font-size: 12px; }
.page-size-input { width: 60px; padding: 3px 6px; font-size: 12px; }
.page-jump { display: flex; align-items: center; gap: 4px; font-size: 12px; }
.jump-input { width: 44px; padding: 3px 4px; border: 1px solid #e7eaec; border-radius: 3px; text-align: center; font-size: 12px; outline: none; }
.jump-btn { padding: 3px 8px; font-size: 12px; }

.empty-state { text-align: center; padding: 60px 20px; color: #9ca3af; }
.empty-state i { font-size: 48px; margin-bottom: 12px; color: #d1d5db; }
.empty-state p { font-size: 14px; margin-bottom: 4px; }
.empty-state .hint { font-size: 12px; color: #d1d5db; }

.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-container { background: #fff; border-radius: 8px; width: 600px; max-height: 80vh; overflow-y: auto; }
.picker-modal { width: 450px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #e7eaec; }
.modal-header h3 { margin: 0; font-size: 16px; }
.close-btn { background: none; border: none; font-size: 22px; cursor: pointer; color: #999; }
.close-btn:hover { color: #333; }
.modal-body { padding: 20px; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
.form-row { display: flex; gap: 15px; margin-bottom: 15px; }
.form-group { flex: 1; }
.form-group label { display: block; font-size: 13px; color: #676a6c; margin-bottom: 5px; font-weight: 500; }
.form-group .form-control { width: 100%; padding: 8px 12px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.search-select { display: flex; gap: 6px; }
.search-select .form-control { flex: 1; cursor: pointer; }
.search-btn { padding: 6px 10px; border: 1px solid #e7eaec; border-radius: 4px; background: #f9f9f9; cursor: pointer; color: #676a6c; }
.search-btn:hover { background: #e7eaec; }
.search-box { margin-bottom: 12px; }
.search-input-wrap { position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 10px; color: #9ca3af; font-size: 13px; z-index: 1; }
.search-box-input { width: 100%; padding: 8px 10px 8px 30px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; outline: none; box-sizing: border-box; transition: border-color 0.2s; }
.search-box-input:focus { border-color: #10b981; }
.picker-list { max-height: 320px; overflow-y: auto; }
.picker-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid #f3f4f6; cursor: pointer; font-size: 13px; color: #374151; transition: background 0.15s; }
.picker-item:hover { background: #f9fafb; }
.picker-item.selected { background: #ecfdf5; color: #059669; font-weight: 500; }
.picker-item i { color: #10b981; font-size: 12px; width: 16px; text-align: center; flex-shrink: 0; }
/* 组织/角色树形层级 */
.tree-level-0 { padding-left: 14px; }
.tree-level-1 { padding-left: 36px; }
.tree-level-2 { padding-left: 58px; }
.tree-level-3 { padding-left: 80px; }
.tree-hint { margin-left: auto; font-size: 10px; color: #d1d5db; }
.picker-empty { text-align: center; padding: 30px 0; color: #9ca3af; font-size: 13px; }

.btn { padding: 6px 14px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-primary { background: #10b981; color: #fff; border-color: #10b981; }
.btn-primary:hover { background: #059669; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }
.btn-success { background: #6366f1; color: #fff; border-color: #6366f1; }
.btn-success:hover { background: #4f46e5; }
.btn-warning { background: #f59e0b; color: #fff; border-color: #f59e0b; }
.btn-warning:hover { background: #d97706; }
.btn-sm { padding: 5px 10px; font-size: 12px; }
.btn-xs { padding: 3px 8px; font-size: 11px; }
</style>
